import en from './en';

// Language display metadata. Deliberately free of the heavy per-language
// translation files so that importing language names (for the selector UI or
// path routing) does not pull ~1MB of translation payload into the bundle.
const LANGUAGE_META = {
  en: { name: 'English', nativeName: 'English' },
  es: { name: 'Spanish', nativeName: 'Español' },
  ja: { name: 'Japanese', nativeName: '日本語' },
  pt: { name: 'Portuguese', nativeName: 'Português' },
  fr: { name: 'French', nativeName: 'Français' },
  zh: { name: 'Chinese', nativeName: '简体中文' },
  ko: { name: 'Korean', nativeName: '한국어' },
  id: { name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  it: { name: 'Italian', nativeName: 'Italiano' },
  nl: { name: 'Dutch', nativeName: 'Nederlands' },
  pl: { name: 'Polish', nativeName: 'Polski' },
  tr: { name: 'Turkish', nativeName: 'Türkçe' },
  vi: { name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  de: { name: 'German', nativeName: 'Deutsch' },
} as const;

export type LanguageCode = keyof typeof LANGUAGE_META;

export const supportedLanguages = Object.keys(LANGUAGE_META) as LanguageCode[];

export const languageNames = Object.entries(LANGUAGE_META).map(([code, { nativeName }]) => ({
  code,
  name: nativeName,
}));

// English is bundled synchronously: it is both the fallback language and the
// most common visitor language, so it must be present on first paint with no
// async round-trip.
//
// The heavy shared namespaces are NOT merged here anymore:
//  - `globalNav` (nav + tools menu strings for the site-wide GlobalNav) is
//    small and registered synchronously for every language in i18n.ts from
//    locales/newLandingNav.ts.
//  - `newLanding` (~90KB × 14 languages) is registered lazily by
//    registerNewLandingI18n.ts, imported by the pages that use it
//    (Landing, DirectorHome).
//  - `lyricsVideo` is registered lazily by registerLyricsVideoI18n.ts,
//    imported by LyricVideoGenerator.
// This keeps ~140KB of translation source out of the entry bundle.
export const enResource = en as Record<string, unknown>;

// Every other language is code-split into its own chunk and fetched on demand
// by i18n's lazy backend (see i18n.ts). This is what keeps the big translation
// files out of the entry bundle.
const loaders: Record<
  Exclude<LanguageCode, 'en'>,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  es: () => import('./es'),
  ja: () => import('./ja'),
  pt: () => import('./pt'),
  fr: () => import('./fr'),
  zh: () => import('./zh'),
  ko: () => import('./ko'),
  id: () => import('./id'),
  it: () => import('./it'),
  nl: () => import('./nl'),
  pl: () => import('./pl'),
  tr: () => import('./tr'),
  vi: () => import('./vi'),
  de: () => import('./de'),
};

// Loads the full translation resource for a language. Returns null for
// unknown codes so the caller can fall back to English. The shared
// globalNav / newLanding / lyricsVideo namespaces are registered separately
// (see comment on enResource) and are deliberately NOT part of this resource:
// i18next's backend store does a shallow top-level merge, so keeping these
// keys out of per-language resources guarantees a late-arriving language
// load can never clobber an already-registered shared namespace.
export async function loadLanguageResource(
  code: string,
): Promise<Record<string, unknown> | null> {
  if (code === 'en') return enResource;
  const loader = loaders[code as Exclude<LanguageCode, 'en'>];
  if (!loader) return null;
  const mod = await loader();
  return mod.default;
}
